import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import {
    CreateSupplierQuotePayload,
    UpdateSupplierQuotePayload,
} from '@Purchasing/Data/DataSource/API/Entity/PurchaseProcess'
import { PurchasingSupplier, SupplierQuote } from '@Purchasing/Domain/Model/PurchaseProcess'
import { usePurchasingSuppliers } from '@Purchasing/Hooks/usePurchasingSuppliers/usePurchasingSuppliers'
import { Field, FormSection, PurchasingDialog } from '@Purchasing/ui/PurchasingDialog'
import { MoneyInput, PercentInput } from '@Purchasing/ui/MoneyInput'
import { centsToInput, formatBRL, realToCents } from '@Purchasing/Utils/money'
import { computeQuoteCost, parsePercent } from '@Purchasing/Utils/quoteCost'
import { zodResolver } from '@hookform/resolvers/zod'
import { Building2, Clock3, Inbox, Loader2, Plus, Search, X } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { Control, Controller, useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'

type QuoteStatus = 'REQUESTED' | 'RECEIVED'

const formSchema = z
    .object({
        status: z.enum(['REQUESTED', 'RECEIVED']),
        quantity: z
            .number({ invalid_type_error: 'Informe a quantidade' })
            .int('Use um número inteiro')
            .positive('Quantidade deve ser maior que zero'),
        unitPrice: z.string().optional(),
        setupFee: z.string().optional(),
        shippingFee: z.string().optional(),
        taxPercent: z.string().optional(),
        otherCosts: z.string().optional(),
        leadTimeDays: z.number().int().positive('Prazo inválido').optional(),
        paymentTerms: z.string().optional(),
        validUntil: z.string().optional(),
        notes: z.string().optional(),
    })
    .superRefine((values, ctx) => {
        // Cotação solicitada ainda não tem preço: os valores entram no
        // "Registrar recebimento", quando o fornecedor responde.
        if (values.status === 'RECEIVED' && realToCents(values.unitPrice) <= 0) {
            ctx.addIssue({ code: 'custom', path: ['unitPrice'], message: 'Informe o preço unitário' })
        }
        const percent = parsePercent(values.taxPercent)
        if (values.taxPercent?.trim() && (percent === null || percent < 0 || percent > 100)) {
            ctx.addIssue({ code: 'custom', path: ['taxPercent'], message: 'Informe um percentual entre 0 e 100' })
        }
    })

type FormValues = z.infer<typeof formSchema>

interface NewSupplierQuoteModalProps {
    isOpen: boolean
    onClose?: () => void
    processId: string
    suggestedQuantity?: number
    onCreate: (payload: CreateSupplierQuotePayload) => Promise<unknown> | null
    /**
     * Modo "registrar recebimento": cotação SOLICITADA já existente. O
     * fornecedor fica fixo; preço/custos são atualizados e ela vira RECEBIDA.
     */
    receivingQuote?: SupplierQuote | null
    onReceive?: (quote: SupplierQuote, payload: UpdateSupplierQuotePayload) => Promise<unknown> | null
}

const emptyValues = (quantity: number): FormValues => ({
    status: 'RECEIVED',
    quantity,
    unitPrice: '',
    setupFee: '',
    shippingFee: '',
    taxPercent: '',
    otherCosts: '',
    leadTimeDays: undefined,
    paymentTerms: '',
    validUntil: '',
    notes: '',
})

const valuesFromQuote = (quote: SupplierQuote): FormValues => ({
    status: 'RECEIVED',
    quantity: quote.quantity,
    unitPrice: centsToInput(quote.unitPriceCents),
    setupFee: centsToInput(quote.setupFeeCents),
    shippingFee: centsToInput(quote.shippingFeeCents),
    taxPercent: quote.taxPercent != null ? String(quote.taxPercent).replace('.', ',') : '',
    otherCosts: centsToInput(quote.otherCostsCents),
    leadTimeDays: quote.leadTimeDays ?? undefined,
    paymentTerms: quote.paymentTerms ?? '',
    validUntil: quote.validUntil ? String(quote.validUntil).slice(0, 10) : '',
    notes: quote.notes ?? '',
})

const FORM_ID = 'supplier-quote-form'

export const NewSupplierQuoteModal = ({
    isOpen,
    onClose,
    processId,
    suggestedQuantity,
    onCreate,
    receivingQuote,
    onReceive,
}: NewSupplierQuoteModalProps) => {
    const receiving = Boolean(receivingQuote)
    const { suppliers, loading: loadingSuppliers, search, createSupplier } = usePurchasingSuppliers()
    const [supplier, setSupplier] = useState<PurchasingSupplier | null>(null)
    const [creatingNewSupplier, setCreatingNewSupplier] = useState(false)
    const [newSupplierName, setNewSupplierName] = useState('')
    const [newSupplierMail, setNewSupplierMail] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [supplierError, setSupplierError] = useState<string | null>(null)

    const { register, handleSubmit, reset, control, setValue, formState: { errors } } = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: emptyValues(suggestedQuantity ?? 1),
    })
    const status = useWatch({ control, name: 'status' }) as QuoteStatus

    // Cada abertura começa limpa (ou com os dados da cotação a receber).
    useEffect(() => {
        if (!isOpen) return
        reset(receivingQuote ? valuesFromQuote(receivingQuote) : emptyValues(suggestedQuantity ?? 1))
        setSupplier(null)
        setCreatingNewSupplier(false)
        setNewSupplierName('')
        setNewSupplierMail('')
        setSupplierError(null)
        setSubmitting(false)
    }, [isOpen, reset, suggestedQuantity, receivingQuote])

    const chooseSupplier = (picked: PurchasingSupplier) => {
        setSupplier(picked)
        setSupplierError(null)
        // Condições padrão do cadastro do fornecedor já entram preenchidas.
        if (picked.paymentTerms) setValue('paymentTerms', picked.paymentTerms)
        if (picked.defaultLeadTimeDays) setValue('leadTimeDays', picked.defaultLeadTimeDays)
    }

    // Checked before react-hook-form validation so a missing supplier is
    // reported together with the field errors, not only after they are fixed.
    const validateSupplier = () => {
        if (receiving || supplier || (newSupplierName && newSupplierMail)) {
            setSupplierError(null)
            return true
        }
        setSupplierError(
            creatingNewSupplier
                ? 'Informe a razão social e o e-mail do novo fornecedor.'
                : 'Selecione um fornecedor ou cadastre um novo.'
        )
        return false
    }

    const costFields = (values: FormValues) => {
        const priced = values.status === 'RECEIVED'
        return {
            quantity: values.quantity,
            unitPriceCents: priced ? realToCents(values.unitPrice) : 0,
            setupFeeCents: priced ? realToCents(values.setupFee) : 0,
            shippingFeeCents: priced ? realToCents(values.shippingFee) : 0,
            taxPercent: priced ? parsePercent(values.taxPercent) : null,
            otherCostsCents: priced ? realToCents(values.otherCosts) : 0,
            leadTimeDays: values.leadTimeDays || undefined,
            paymentTerms: values.paymentTerms || undefined,
            validUntil: values.validUntil || undefined,
            notes: values.notes || undefined,
        }
    }

    const submitForm = handleSubmit(async (values) => {
        if (receivingQuote) {
            setSubmitting(true)
            const result = await onReceive?.(receivingQuote, costFields(values))
            setSubmitting(false)
            if (result) onClose?.()
            return
        }
        if (!validateSupplier()) return
        let supplierId = supplier?._id

        if (!supplierId) {
            const created = await createSupplier({ corporateName: newSupplierName, mail: newSupplierMail })
            if (!created) return
            supplierId = created._id
        }
        if (!supplierId || !processId) return

        setSubmitting(true)
        const result = await onCreate({ supplierId, status: values.status, ...costFields(values) })
        setSubmitting(false)
        // Em erro o modal continua aberto com os dados para corrigir.
        if (result) onClose?.()
    })

    const supplierName = receivingQuote?.supplierSnapshot?.corporateName ?? 'fornecedor'
    const submitLabel = submitting
        ? 'Salvando…'
        : receiving
          ? 'Confirmar recebimento'
          : status === 'REQUESTED'
            ? 'Registrar solicitação'
            : 'Registrar cotação'

    return (
        <PurchasingDialog
            isOpen={isOpen}
            onClose={onClose}
            size="lg"
            testId="supplier-quote-dialog"
            eyebrow={receiving ? `Cotação · ${supplierName}` : 'Cotação'}
            title={receiving ? 'Registrar cotação recebida' : 'Nova cotação'}
            description={
                receiving
                    ? `Informe os valores enviados por ${supplierName}. A cotação passa para Recebida e pode ser selecionada como vencedora.`
                    : 'Registre o pedido de cotação enviado ao fornecedor ou a proposta que ele já respondeu.'
            }
            footer={
                <>
                    <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
                    <Button type="submit" form={FORM_ID} disabled={submitting} className="min-w-[180px]">
                        {submitLabel}
                    </Button>
                </>
            }
        >
            <form
                id={FORM_ID}
                noValidate
                onSubmit={(event) => {
                    validateSupplier()
                    return submitForm(event)
                }}
            >
                {!receiving && (
                    <FormSection title="Fornecedor">
                        {creatingNewSupplier ? (
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <Field label="Razão social" htmlFor="newSupplierName">
                                    <Input id="newSupplierName" name="newSupplierName" aria-label="Razão Social do novo fornecedor"
                                        value={newSupplierName} onChange={(e) => setNewSupplierName(e.target.value)} />
                                </Field>
                                <Field label="E-mail" htmlFor="newSupplierMail">
                                    <Input id="newSupplierMail" name="newSupplierMail" type="email" aria-label="E-mail do novo fornecedor"
                                        value={newSupplierMail} onChange={(e) => setNewSupplierMail(e.target.value)} />
                                </Field>
                            </div>
                        ) : supplier ? (
                            <div className="flex items-center gap-3 rounded-md border border-brand bg-accent px-3 py-2.5" data-testid="quote-supplier-selected">
                                <Building2 className="h-5 w-5 shrink-0 text-brand-deep" strokeWidth={1.5} />
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-semibold">{supplier.corporateName}</p>
                                    <p className="truncate text-xs text-muted-foreground">
                                        {[supplier.cnpj, supplier.mail].filter(Boolean).join(' · ') || 'Sem CNPJ/e-mail no cadastro'}
                                    </p>
                                </div>
                                <Button type="button" size="sm" variant="ghost" onClick={() => setSupplier(null)}>Trocar</Button>
                            </div>
                        ) : (
                            <SupplierSearch suppliers={suppliers} loading={loadingSuppliers} onSearch={search} onPick={chooseSupplier} />
                        )}
                        <div className="mt-2 flex items-center justify-between gap-2">
                            <button
                                type="button"
                                className="inline-flex items-center gap-1 text-sm font-semibold text-brand-pink hover:underline"
                                onClick={() => { setCreatingNewSupplier(!creatingNewSupplier); setSupplier(null); setSupplierError(null) }}
                                data-testid="new-supplier-toggle"
                            >
                                {creatingNewSupplier ? 'Escolher fornecedor existente' : <><Plus className="h-4 w-4" />Cadastrar novo fornecedor</>}
                            </button>
                        </div>
                        {supplierError && (
                            <p className="mt-1 text-sm text-od-secondary-600" role="alert" data-testid="quote-supplier-error">{supplierError}</p>
                        )}
                    </FormSection>
                )}

                {!receiving && (
                    <FormSection title="Situação da cotação">
                        <Controller
                            control={control}
                            name="status"
                            render={({ field }) => (
                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Status da cotação" data-testid="quote-status-select">
                                    {([
                                        ['RECEIVED', 'Recebida', 'O fornecedor já respondeu com preço', Inbox],
                                        ['REQUESTED', 'Solicitada', 'Pedido enviado, aguardando resposta', Clock3],
                                    ] as const).map(([value, label, hint, Icon]) => (
                                        <button
                                            key={value}
                                            type="button"
                                            role="radio"
                                            aria-checked={field.value === value}
                                            onClick={() => field.onChange(value)}
                                            className={cn(
                                                'flex items-start gap-3 rounded-md border px-3 py-2.5 text-left transition-colors',
                                                field.value === value ? 'border-brand bg-accent' : 'border-border hover:bg-secondary'
                                            )}
                                        >
                                            <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', field.value === value ? 'text-brand-deep' : 'text-muted-foreground')} strokeWidth={1.5} />
                                            <span>
                                                <span className="block text-sm font-semibold">{label}</span>
                                                <span className="block text-xs text-muted-foreground">{hint}</span>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        />
                    </FormSection>
                )}

                <FormSection
                    title="Proposta"
                    hint={status === 'REQUESTED' ? 'Os valores entram depois, em “Registrar recebimento”, quando o fornecedor responder.' : undefined}
                >
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                        <Field label="Quantidade" htmlFor="quantity" error={errors.quantity?.message}>
                            <Input id="quantity" type="number" min={1} step={1} aria-label="Quantidade cotada" className="text-right tabular-nums"
                                {...register('quantity', { valueAsNumber: true })} />
                        </Field>
                        {status === 'RECEIVED' && (
                            <Field label="Preço unitário" htmlFor="unitPrice" error={errors.unitPrice?.message} className="col-span-1">
                                <MoneyField control={control} name="unitPrice" label="Preço unitário" invalid={Boolean(errors.unitPrice)} />
                            </Field>
                        )}
                        <Field label="Prazo (dias)" htmlFor="leadTimeDays" error={errors.leadTimeDays?.message}>
                            <Input id="leadTimeDays" type="number" min={1} aria-label="Prazo (dias)" className="text-right tabular-nums"
                                {...register('leadTimeDays', { setValueAs: (v: any) => (v === '' || v == null ? undefined : Number(v)) })} />
                        </Field>
                        <Field label="Válida até" htmlFor="validUntil">
                            <Input id="validUntil" type="date" aria-label="Válida até" {...register('validUntil')} />
                        </Field>
                    </div>
                </FormSection>

                {status === 'RECEIVED' && (
                    <FormSection title="Custos adicionais" hint="Frete, setup e outros entram no total e são diluídos no custo unitário. O DIFAL incide sobre o valor dos produtos.">
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                            <Field label="Setup" htmlFor="setupFee">
                                <MoneyField control={control} name="setupFee" label="Setup" />
                            </Field>
                            <Field label="Frete" htmlFor="shippingFee">
                                <MoneyField control={control} name="shippingFee" label="Frete" />
                            </Field>
                            <Field label="DIFAL" htmlFor="taxPercent" error={errors.taxPercent?.message}>
                                <Controller control={control} name="taxPercent" render={({ field }) => (
                                    <PercentInput id="taxPercent" name="taxPercent" aria-label="DIFAL (%)" value={field.value ?? ''}
                                        onChange={field.onChange} invalid={Boolean(errors.taxPercent)} />
                                )} />
                            </Field>
                            <Field label="Outros" htmlFor="otherCosts">
                                <MoneyField control={control} name="otherCosts" label="Outros custos" />
                            </Field>
                        </div>
                        <CostSummary control={control} />
                    </FormSection>
                )}

                <FormSection title="Condições">
                    <div className="grid grid-cols-1 gap-3">
                        <Field label="Condição de pagamento" htmlFor="paymentTerms">
                            <Input id="paymentTerms" aria-label="Condição de pagamento" placeholder="Ex.: 50% no pedido, 50% na entrega" {...register('paymentTerms')} />
                        </Field>
                        <Field label="Observações" htmlFor="notes">
                            <Textarea id="notes" aria-label="Observações" rows={2} {...register('notes')} />
                        </Field>
                    </div>
                </FormSection>
            </form>
        </PurchasingDialog>
    )
}

type MoneyName = 'unitPrice' | 'setupFee' | 'shippingFee' | 'otherCosts'

const MoneyField = ({ control, name, label, invalid }: { control: Control<FormValues>; name: MoneyName; label: string; invalid?: boolean }) => (
    <Controller control={control} name={name} render={({ field }) => (
        <MoneyInput id={name} name={name} aria-label={label} value={field.value ?? ''} onChange={field.onChange} invalid={invalid} />
    )} />
)

const SupplierSearch = ({ suppliers, loading, onSearch, onPick }: {
    suppliers: PurchasingSupplier[]
    loading: boolean
    onSearch: (query: string) => void
    onPick: (supplier: PurchasingSupplier) => void
}) => {
    const [query, setQuery] = useState('')
    const timer = useRef<ReturnType<typeof setTimeout>>()
    const listId = useId()

    const handleChange = (value: string) => {
        setQuery(value)
        clearTimeout(timer.current)
        timer.current = setTimeout(() => onSearch(value.trim()), 300)
    }
    useEffect(() => () => clearTimeout(timer.current), [])

    const results = query.trim() ? suppliers.slice(0, 6) : []
    return (
        <div>
            <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
                <Input name="supplierSearch" aria-label="Buscar fornecedor por nome ou CNPJ" aria-controls={listId} placeholder="Buscar fornecedor por nome ou CNPJ"
                    className="pl-9" value={query} onChange={(e) => handleChange(e.target.value)} />
                {loading && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />}
                {query && !loading && (
                    <button type="button" aria-label="Limpar busca" className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground" onClick={() => handleChange('')}>
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>
            {query.trim() && (
                <ul id={listId} role="listbox" aria-label="Fornecedores encontrados" data-testid="quote-supplier-select" className="mt-1 max-h-56 overflow-y-auto rounded-md border border-border">
                    {results.length === 0 && !loading && (
                        <li className="px-3 py-2 text-sm text-muted-foreground">Nenhum fornecedor encontrado.</li>
                    )}
                    {results.map((item) => (
                        <li key={item._id}>
                            <button type="button" role="option" aria-selected={false} onClick={() => onPick(item)}
                                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-accent">
                                <span className="truncate font-medium">{item.corporateName}</span>
                                {item.cnpj && <span className="shrink-0 text-xs text-muted-foreground">{item.cnpj}</span>}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

const CostSummary = ({ control }: { control: Control<FormValues> }) => {
    const [quantity, unitPrice, setupFee, shippingFee, otherCosts, taxPercentRaw] = useWatch({
        control,
        name: ['quantity', 'unitPrice', 'setupFee', 'shippingFee', 'otherCosts', 'taxPercent'],
    })
    const taxPercent = parsePercent(taxPercentRaw)
    const cost = computeQuoteCost({
        quantity: Number(quantity) || 0,
        unitPriceCents: realToCents(unitPrice),
        setupFeeCents: realToCents(setupFee),
        shippingFeeCents: realToCents(shippingFee),
        otherCostsCents: realToCents(otherCosts),
        taxPercent,
    })
    const row = (label: string, value: string) => (
        <div className="flex items-baseline justify-between gap-3 text-sm">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="tabular-nums">{value}</dd>
        </div>
    )
    return (
        <div className="mt-4 grid grid-cols-1 gap-4 rounded-md bg-secondary p-4 sm:grid-cols-[1fr_auto]" data-testid="quote-cost-summary">
            <dl className="space-y-1">
                {row(`Produtos (${Number(quantity) || 0} × ${formatBRL(realToCents(unitPrice))})`, formatBRL(cost.subtotalCents))}
                {row(`DIFAL${taxPercent != null ? ` (${String(taxPercent).replace('.', ',')}%)` : ''}`, formatBRL(cost.taxCents))}
                {row('Setup + frete + outros', formatBRL(cost.additionalCents))}
                <div className="flex items-baseline justify-between gap-3 border-t border-border pt-1 text-sm font-semibold">
                    <dt>Total</dt>
                    <dd className="tabular-nums">{formatBRL(cost.totalCents)}</dd>
                </div>
            </dl>
            <div className="flex flex-col items-start justify-center rounded-md bg-background px-4 py-3 sm:items-end">
                <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">Custo unitário final</span>
                <span className="font-display text-2xl font-extrabold text-brand-deep tabular-nums" data-testid="quote-unit-cost">{formatBRL(cost.unitCostCents)}</span>
                <span className="text-xs text-muted-foreground">com DIFAL e custos diluídos</span>
            </div>
        </div>
    )
}
